import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ClipboardDocumentListIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  useLocationMedicationsDueQuery,
  useListStudentMedicationsQuery,
  useLocalGetStudentsQuery,
  useEndMedicationMutation,
} from '../../store/api/localApi';
import { useLocationContext } from '../../hooks/useLocationContext';
import { ScopedLink } from '../../components/ScopedLink';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useToast } from '../../components/Toast';
import {
  MedicationAdministrationEntry,
  MedicationRequirementStatus,
  MedicationRequirementType,
  MedicationRequirementWithEntries,
  Student,
} from '../../types';
import { studentDisplayName } from '../../utils/student';
import { dublinDateString } from '../../utils/date';
import {
  ROUTE_LABELS,
  STATUS_LABELS,
  formatDate,
} from '../../components/medications/medication-labels';

// A course can be ended only while it is still live. ENDED and REVOKED are the
// server's terminal states (mirrors ParentChildMedications' TERMINAL_STATUSES),
// and EXPIRED_MEDICATION is already finished by expiry — none of them are
// endable. PENDING_CONSENT and ACTIVE are the two the backend still accepts an
// end for, so the affordance is offered for both.
const ENDABLE_STATUSES: MedicationRequirementStatus[] = [
  MedicationRequirementStatus.PENDING_CONSENT,
  MedicationRequirementStatus.ACTIVE,
];

/**
 * The children at this location who have any medication on file.
 *
 * Derived from the location's own due board rather than from the full student
 * roster: the board is already the location-scoped, authorization-gated view of
 * who has medication here, and walking every enrolled child instead would mean
 * one request per child at a service with a hundred of them to find the four
 * who are on something.
 *
 * Both halves of the board count. An EMERGENCY_ONLY requirement appears only as
 * a badge — a child who carries an EpiPen and nothing else has no slot at all,
 * and reading `slots` alone would leave them off the register entirely.
 */
function childIdsWithMedication(
  slots: Array<{ childId: string }> | undefined,
  badges: Array<{ childId: string }> | undefined
): string[] {
  return Array.from(new Set([...(slots ?? []), ...(badges ?? [])].map((r) => r.childId)));
}

/**
 * GET /locations/{locationId}/medications-due + per-child records — the
 * location-wide medication register.
 *
 * THIS IS THE THIRD OF THE THREE SURFACES that must flag an unwitnessed dose.
 * The spec requires it in the child's log, this register, and the print
 * output; two out of three is a gap an inspector would find. A past-expiry
 * emergency dose is flagged here for the same reason.
 *
 * Both flags are derived from entries already on the per-child record, so the
 * register agrees with the log by construction rather than by a second
 * server-side computation that could drift from it.
 *
 * COMPOSED FROM EXISTING ENDPOINTS ON PURPOSE. There is no location-register
 * endpoint, and adding one would be a new API contract on a live product for a
 * read two existing endpoints already answer. The composition is bounded by the
 * number of children at a location who are on medication, which is small.
 */
const LocationMedicationRegister: React.FC = () => {
  // Same three-URL-shape resolution as IncidentRegister: flat
  // /locations/:locationId/medications, /loc/:locId/medications (org-admin
  // scoped in), /org/:orgId/loc/:locId/medications (super-admin scoped in).
  const params = useParams<{ locationId?: string; locId?: string }>();
  const { activeLocationId } = useLocationContext();
  // Once per render, not per row — see RegisterRow's `today` prop.
  const today = dublinDateString();
  const locationId = params.locationId || params.locId || activeLocationId || undefined;

  const {
    data: board,
    isLoading: isBoardLoading,
    isError: isBoardError,
  } = useLocationMedicationsDueQuery(locationId!, { skip: !locationId });

  const { data: students } = useLocalGetStudentsQuery(locationId ?? '', { skip: !locationId });

  const childIds = useMemo(
    () => childIdsWithMedication(board?.slots, board?.badges),
    [board?.slots, board?.badges]
  );

  if (!locationId) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Invalid URL
      </div>
    );
  }

  if (isBoardLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div
          className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"
          data-testid="medication-register-loading"
        />
      </div>
    );
  }

  if (isBoardError) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-sm text-slate-500"
        data-testid="medication-register-error"
      >
        Failed to load the medication register.
      </div>
    );
  }

  return (
    <div data-testid="medication-register" className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2 mb-4">
        <ClipboardDocumentListIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        Medication register
      </h1>

      {childIds.length === 0 ? (
        <div
          data-testid="medication-register-empty"
          className="mt-12 text-center text-sm text-gray-500"
        >
          No child at this location has medication on file.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-100">
          {childIds.map((childId) => (
            <ChildMedicationRows
              key={childId}
              childId={childId}
              childName={studentDisplayName(students?.find((s: Student) => s.id === childId))}
              today={today}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * One child's requirements as register rows.
 *
 * The per-child fetch is what supplies the ENTRIES, and the entries are what
 * the two flags are derived from — the due board carries neither. This is
 * `useListStudentMedicationsQuery`, the same staff endpoint the child's own
 * medication screen uses, so the register and the log cannot disagree.
 */
const ChildMedicationRows: React.FC<{ childId: string; childName: string; today: string }> = ({
  childId,
  childName,
  today,
}) => {
  const { data } = useListStudentMedicationsQuery({ studentId: childId });
  const requirements = data?.requirements ?? [];

  if (requirements.length === 0) return null;

  return (
    <>
      {requirements.map((requirement) => (
        <RegisterRow
          key={requirement.requirementId}
          requirement={requirement}
          childId={childId}
          childName={childName}
          today={today}
        />
      ))}
    </>
  );
};

const RegisterRow: React.FC<{
  requirement: MedicationRequirementWithEntries;
  childId: string;
  childName: string;
  /** Dublin civil date, computed once by the page — not per row. */
  today: string;
}> = ({ requirement, childId, childName, today }) => {
  const { showSuccess, showError } = useToast();
  const [endMedication, { isLoading: isEnding }] = useEndMedicationMutation();
  const [confirmingEnd, setConfirmingEnd] = useState(false);

  const entries: MedicationAdministrationEntry[] = requirement.entries ?? [];

  // A course is endable only from a live status; ended/revoked/expired courses
  // are terminal and the server would reject the end. Gating the affordance in
  // the UI keeps the register honest — the backend still enforces both the
  // status and the `medication:end` capability regardless.
  const isEndable = ENDABLE_STATUSES.includes(requirement.status);

  const handleEndCourse = async () => {
    try {
      await endMedication({ medicationId: requirement.requirementId }).unwrap();
      showSuccess('Course ended.');
      setConfirmingEnd(false);
    } catch (err: unknown) {
      const data =
        typeof err === 'object' && err !== null && 'data' in err
          ? (err as { data?: { error?: string; message?: string } }).data
          : undefined;
      showError(data?.error || data?.message || 'Could not end this course.');
    }
  };

  // `unwitnessedReason` is the recorded fact, not a missing witness name: the
  // server requires one whenever a dose is given without a second signature
  // (WITNESS_OR_REASON_REQUIRED), so its presence is the reliable signal and an
  // absent witnessStaffName is not.
  const unwitnessedCount = entries.filter((e) => e.unwitnessedReason).length;
  const pastExpiryCount = entries.filter((e) => e.administeredPastExpiry).length;

  return (
    <div
      data-testid={`medication-register-row-${requirement.requirementId}`}
      className={`px-4 py-3 border-l-4 ${
        pastExpiryCount > 0
          ? 'border-l-rose-600 bg-rose-50'
          : unwitnessedCount > 0
          ? 'border-l-amber-500 bg-amber-50'
          : 'border-l-transparent'
      }`}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <ScopedLink
          to={`/students/${childId}`}
          data-testid={`medication-register-row-${requirement.requirementId}-child`}
          className="text-gray-900 font-medium hover:underline"
        >
          {childName}
        </ScopedLink>
        <span className="text-gray-700">
          {requirement.medicationName} · {requirement.dosage}
        </span>
        <span className="text-gray-500 whitespace-nowrap">{ROUTE_LABELS[requirement.route]}</span>
        <span
          data-testid={`medication-register-row-${requirement.requirementId}-status`}
          className="text-xs font-semibold text-gray-600 whitespace-nowrap"
        >
          {STATUS_LABELS[requirement.status]}
        </span>

        {unwitnessedCount > 0 && (
          <span
            data-testid="register-unwitnessed-flag"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-200 text-amber-900"
          >
            <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {unwitnessedCount === 1 ? 'Unwitnessed dose' : `${unwitnessedCount} unwitnessed doses`}
          </span>
        )}

        {pastExpiryCount > 0 && (
          <span
            data-testid="register-past-expiry-flag"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-600 text-white"
          >
            <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {pastExpiryCount === 1
              ? 'Dose given past expiry'
              : `${pastExpiryCount} doses given past expiry`}
          </span>
        )}

        {/* The child's attending days changed after this schedule was agreed, so
            a dose may now fall on a day they are not booked in. Staff need to see
            this at the trolley, not only the guardian in the parent app. */}
        {requirement.needsScheduleReview && (
          <span
            data-testid="medication-needs-review-flag"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200"
          >
            <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Schedule needs review
          </span>
        )}

        <ScopedLink
          to={`/students/${childId}/medications/print`}
          data-testid={`medication-register-row-${requirement.requirementId}-print`}
          className="ml-auto text-xs text-primary-600 hover:text-primary-500 whitespace-nowrap"
        >
          Printable record
        </ScopedLink>

        {isEndable && (
          <button
            type="button"
            data-testid={`end-medication-${requirement.requirementId}`}
            onClick={() => setConfirmingEnd(true)}
            disabled={isEnding}
            className="text-xs font-medium text-rose-600 hover:text-rose-500 whitespace-nowrap disabled:opacity-50"
          >
            End course
          </button>
        )}
      </div>

      <p className="mt-0.5 text-xs text-gray-500">
        {requirement.requirementType === MedicationRequirementType.EMERGENCY_ONLY
          ? 'Emergency use only'
          : requirement.schedule
          ? requirement.schedule.timesOfDay.join(', ')
          : 'No schedule'}
        {' · from '}
        {formatDate(requirement.startDate)}
        {requirement.endDate ? ` to ${formatDate(requirement.endDate)}` : ''}
        {requirement.medicationExpiryDate ? ' · ' : ''}
        {requirement.medicationExpiryDate &&
          (requirement.medicationExpiryDate < today ? (
            <span
              data-testid={`register-expired-${requirement.requirementId}`}
              className="font-semibold text-red-700"
            >
              medication expired {formatDate(requirement.medicationExpiryDate)}
            </span>
          ) : (
            <>medication expires {formatDate(requirement.medicationExpiryDate)}</>
          ))}
      </p>

      <ConfirmDialog
        isOpen={confirmingEnd}
        title="End medication course"
        message={`End ${requirement.medicationName} for ${childName}? This records the course as finished and stops further scheduled doses. It cannot be undone.`}
        confirmLabel="End course"
        variant="danger"
        isLoading={isEnding}
        confirmTestId={`end-medication-${requirement.requirementId}-confirm`}
        onConfirm={handleEndCourse}
        onCancel={() => setConfirmingEnd(false)}
      />
    </div>
  );
};

export default LocationMedicationRegister;
